<script lang="ts">
  // Lyktan map view. Wraps MapLibre GL JS with Protomaps vector tiles and
  // centers initially on Hisings Backa.
  //
  // Implementation notes:
  // - Svelte 5 runes (`$state`, `$effect`) drive lifecycle. No persistent
  //   state.
  // - The MapLibre instance is created lazily in `$effect` so SSR/jsdom
  //   environments (vitest) can import this component without crashing.
  // - When the Protomaps API key is missing we render a localised error
  //   panel instead of mounting MapLibre, so the user never sees an empty
  //   black canvas.

  import { onDestroy } from 'svelte';
  import maplibregl, { type Map as MapLibreMap } from 'maplibre-gl';
  import 'maplibre-gl/dist/maplibre-gl.css';
  import { INITIAL_CENTER, INITIAL_ZOOM, readProtomapsApiKey } from '../config/map';
  import { vectorDarkStyle } from '../config/mapStyles';

  interface Props {
    /** Override the API key in tests. Defaults to reading import.meta.env. */
    apiKey?: string | null;
    /**
     * Called when the underlying MapLibre instance becomes available or is
     * torn down. Lets the parent (e.g. hearth drawing controller in
     * S-04-04) attach layers and event listeners without poking at the
     * DOM directly.
     */
    // eslint-disable-next-line no-unused-vars
    onMapReady?: (map: MapLibreMap | null) => void;
    /**
     * Admin-konfigurerade default-värden från app_settings (S-04-22).
     * Faller tillbaka på kompilerings-default (Hisingsbacka centrum) om
     * inte angivet. Sätts av App-shellen efter `GET /api/admin/settings`
     * vid session-start.
     */
    initialCenter?: { lng: number; lat: number };
    initialZoom?: number;
  }

  const { apiKey: apiKeyProp, onMapReady, initialCenter, initialZoom }: Props = $props();
  const apiKey: string | null = $derived(
    apiKeyProp !== undefined ? apiKeyProp : readProtomapsApiKey(),
  );

  let mapContainer: HTMLDivElement | undefined = $state();
  let mapInstance: MapLibreMap | null = null;

  $effect(() => {
    if (!mapContainer) return;
    if (!apiKey) return;

    const center = initialCenter ?? INITIAL_CENTER;
    const zoom = initialZoom ?? INITIAL_ZOOM;
    const map = new maplibregl.Map({
      container: mapContainer,
      style: vectorDarkStyle(apiKey),
      center: [center.lng, center.lat],
      zoom,
      // Disable telemetry and any opt-in cloud features. MapLibre is
      // already self-hosted-friendly, but we set explicit flags to make
      // the intent obvious to reviewers.
      attributionControl: { compact: true },
    });

    mapInstance = map;
    // Notify the parent only after the style has loaded so consumers
    // can safely call `addSource` / `addLayer`. MapLibre throws if you
    // touch a style before `style.load`.
    map.once('load', () => {
      onMapReady?.(map);
    });

    return () => {
      onMapReady?.(null);
      map.remove();
      mapInstance = null;
    };
  });

  onDestroy(() => {
    mapInstance?.remove();
    mapInstance = null;
  });
</script>

{#if !apiKey}
  <div class="map-error" role="alert">
    <h3>Karta otillgänglig</h3>
    <p>Saknas API-nyckel för kartleverantören. Kontakta administratören.</p>
  </div>
{:else}
  <div class="map-shell">
    <div class="map-canvas" bind:this={mapContainer} aria-label="Karta över Hisings Backa"></div>
  </div>
{/if}

<style>
  .map-shell {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 480px;
  }
  .map-canvas {
    position: absolute;
    inset: 0;
    background: var(--bg);
  }
  .map-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 3rem 1.5rem;
    color: var(--text-dim);
    background: var(--bg-elev);
    border: 1px solid var(--rule);
    border-radius: 4px;
    min-height: 240px;
  }
  .map-error h3 {
    margin: 0 0 0.5rem;
    font-size: 1.1rem;
    color: var(--text);
  }
  .map-error p {
    margin: 0;
    color: var(--text-dim);
    max-width: 32em;
  }
</style>
